import { toTablePagination } from '@sparklayer-io/spark-dash-ui/utils/pagination';
import { Card, EmptyState } from '@ui/themed';
import { t } from 'i18next';
import { type ReactNode, useMemo, useState } from 'react';
import { SearchInput } from '../../../components/search-input';
import { SparkSuspense } from '../../../components/spark-suspense';
import { EXCLUDED_DEFAULT_SALES_AGENT_EMAIL } from '../constants';
import { useSearchCustomers } from '../hooks/use-search-customers';
import type { CustomerSearchQuery } from '../types/api';
import { CustomersTable, type CustomersTableProps } from './customers-table';

type CustomersSearchTableEmptyState = {
  title: string;
  subtitle?: string;
  imageType?: 'customerGroup' | 'priceList' | 'shippingMethod' | 'errorState' | 'salesAgent';
  children?: ReactNode;
};

type CustomersSearchTableProps = Omit<CustomersTableProps, 'pagination' | 'customers'> & {
  baseSearchRequest: CustomerSearchQuery;
  /** Shown when no customers match the search. Omit to show the table with no rows. */
  emptyState?: CustomersSearchTableEmptyState;
};

export function CustomersSearchTable({
  baseSearchRequest,
  emptyState,
  ...tableProps
}: CustomersSearchTableProps): JSX.Element {
  const [searchValue, setSearchValue] = useState(baseSearchRequest.search_term || '');

  const searchQuery = useMemo(() => {
    return {
      ...baseSearchRequest,
      ...(searchValue ? { search_term: searchValue } : {}),
    };
  }, [baseSearchRequest, searchValue]);

  const query = useSearchCustomers(searchQuery);

  // Since the following account is created as a sales agent by default but not included in the
  // sales agent counts, and we need the empty state shown hide the account accept if searched for!
  const customers = searchValue
    ? query.data
    : query.data?.filter((x) => x.email !== EXCLUDED_DEFAULT_SALES_AGENT_EMAIL);

  const showEmptyState = emptyState && (customers?.length ?? 0) === 0 && !query.isLoading;

  return (
    <SparkSuspense
      onReload={query.refetch}
      isError={query.isError}
      errorMessage={t('features:customers.components.customers_search_table.load_error')}
      isLoading={query.isLoading}
    >
      {(searchValue.length > 0 || (customers && customers.length > 1)) && (
        <SearchInput
          onSearchChange={setSearchValue}
          placeholder={t(
            'features:customers.components.customers_search_table.search_bar.placeholder',
          )}
        />
      )}
      {showEmptyState ? (
        <Card>
          <EmptyState
            title={emptyState.title}
            subtitle={emptyState.subtitle}
            imageType={emptyState.imageType ?? 'customerGroup'}
          >
            {emptyState.children}
          </EmptyState>
        </Card>
      ) : (
        <CustomersTable
          customers={customers ?? []}
          pagination={toTablePagination(query, customers?.length ?? 0)}
          {...tableProps}
        />
      )}
    </SparkSuspense>
  );
}
